﻿import { createFileRoute, Link } from "@tanstack/solid-router";
import { z } from "zod";
import { UserAuthForm } from "~/components/auth/user-auth-form";
import { IconLogo } from "~/components/icons";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign In | ActShark" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "googlebot", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: z.object({
    mode: z.enum(["login", "register"]).optional(),
    verified: z.enum(["0", "1"]).optional(),
    message: z.string().optional(),
    email: z.string().optional(),
  }),
  component: LoginPage,
});

function LoginPage() {
  return (
    <div class="min-h-screen lg:grid lg:grid-cols-2">
      {/* -- Left panel - hero gradient --------------------------------------- */}
      <div class="hero-bg relative hidden h-full flex-col p-10 lg:flex">
        {/* Grid overlay */}
        <div
          class="pointer-events-none absolute inset-0"
          style="background-image: linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px); background-size: 64px 64px;"
        />

        {/* Logo */}
        <Link to="/" class="flex items-center gap-3 shrink-0">
          <IconLogo class="size-9" />
          <div class="flex flex-col justify-center">
            <span
              class={
                "text-base font-semibold leading-none tracking-tight text-white"
              }
            >
              ActShark
            </span>
            <span
              class={
                "mt-1 text-[10px] font-medium uppercase leading-none tracking-[0.2em] text-white/60"
              }
            >
              accounting
            </span>
          </div>
        </Link>

        {/* Marketing copy */}
        <div class="relative z-20 mt-auto space-y-4">
          <h2 class="text-4xl font-bold leading-tight tracking-tight text-white">
            Cloud
            <br />
            Accounting Software
          </h2>
          <p class="text-lg leading-snug text-white/70">
            Own your financial data with zero vendor lock-in - AI-powered
            extraction built in.
          </p>
        </div>
      </div>

      {/* -- Right panel - form ----------------------------------------------- */}
      <div class="flex min-h-screen flex-col lg:min-h-0">
        {/* Mobile top bar - logo left, Home right. Hidden on lg+ */}
        <div class="flex items-center justify-between px-6 py-4 lg:hidden">
          <Link to="/" class="flex items-center gap-3 shrink-0">
            <IconLogo class="size-9" fill="currentColor" />
            <div class="flex flex-col justify-center">
              <span
                class={"text-base font-semibold leading-none tracking-tight"}
              >
                ActShark
              </span>
              <span
                class={
                  "mt-1 text-[10px] font-medium uppercase leading-none tracking-[0.2em] text-muted-foreground"
                }
              >
                accounting
              </span>
            </div>
          </Link>
          <Link
            to="/"
            class="inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Home
          </Link>
        </div>

        {/* Form - vertically centered in remaining space */}
        <div class="flex flex-1 items-center justify-center p-8">
          <div class="mx-auto flex w-full max-w-sm flex-col gap-6">
            <UserAuthForm />

            <p class="text-center text-sm text-muted-foreground">
              By continuing, you agree to our{" "}
              <Link
                to="/terms"
                class="underline underline-offset-4 hover:text-primary"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                class="underline underline-offset-4 hover:text-primary"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Desktop Home button - absolute over left panel area */}
      <Link
        to="/"
        class="absolute right-8 top-8 hidden lg:inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        Home
      </Link>
    </div>
  );
}
